import { useCreateBlockNote } from '@blocknote/react'
import BlockNote from './BlockNote'
import { BlockNoteSchema, defaultBlockSpecs } from '@blocknote/core'
import { useCallback, useEffect, useMemo } from 'react'
import { useSmartTranslation } from '../hooks/useSmartTranslation'
import * as locales from '@blocknote/core/locales'
import { Card, Typography, Box, Divider } from '@mui/material'
import { localeCodeOf } from '../utilities/customLanguage'

// Create a version of the specs without the media types
const { image, video, audio, file, ...allowedBlocks } = defaultBlockSpecs
const customSchema = BlockNoteSchema.create({
  blockSpecs: allowedBlocks
})

const useSmartTextArea = (initialContent) => {
  const { i18n } = useSmartTranslation()

  const dictionary = useMemo(() => {
    return locales[localeCodeOf(i18n.language)] || locales.en
  }, [i18n.language])

  const content = useMemo(() => {
    if (!initialContent) return undefined
    if (Array.isArray(initialContent)) return initialContent
    if (typeof initialContent !== 'string') return undefined

    try {
      const parsed = JSON.parse(initialContent)
      if (Array.isArray(parsed)) return parsed
      if (Array.isArray(parsed?.blocks)) return parsed.blocks
      return undefined
    } catch (_) {
      return [{ type: 'paragraph', content: [{ type: 'text', text: initialContent }] }]
    }
  }, [initialContent])

  const editor = useCreateBlockNote({
    schema: customSchema,
    dictionary
  }, [dictionary])

  useEffect(() => {
    if (content && editor) {
      editor.replaceBlocks(editor.document, content)
    }
  }, [content, editor])

  const getData = useCallback(() => {
    return JSON.stringify(editor.document)
  }, [editor?.document])

  const resetInitialContent = () => {
    editor.replaceBlocks(editor.document, [{ type: 'paragraph' }])
  }
  return { editor, getData, resetInitialContent }
}

const SmartTextArea = (props) => {
  // 1. Destructure readOnly
  const { editor, title, readOnly } = props
  const { i18n } = useSmartTranslation()

  return (
    <Card
      variant='outlined'
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'visible',
        borderRadius: 2,
        transition: 'box-shadow 0.2s ease-in-out, border-color 0.2s ease-in-out',

        // 2. Only apply interactive styles if NOT read-only
        ...(!readOnly && {
          '&:hover': {
            boxShadow: (theme) => theme.shadows[2]
          },
          '&:focus-within': {
            borderColor: 'primary.main',
            boxShadow: (theme) => `0 0 0 1px ${theme.palette.primary.main}`,

            '& .smart-title': {
              color: 'primary.main'
            }
          }

        })
      }}
    >
      {/* Header */}
      {title && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', backgroundColor: readOnly ? 'background.default' : 'transparent', padding: '0.5rem' }}>
            <Typography variant='h6' color='primary.text' className='smart-title'>
              {title}
            </Typography>
          </Box>
          <Divider />
        </>
      )}

      {/* Relative Container */}
      <Box sx={{ flexGrow: 1, minHeight: 0, position: 'relative' }}>
        {/* 3. Pass readOnly down to your BlockNote component */}
        <BlockNote editor={editor} readOnly={readOnly} key={i18n.language} />
      </Box>
    </Card>
  )
}

export { useSmartTextArea, SmartTextArea }
